/* eslint-disable react/prop-types */
import { useDispatch, useSelector } from "react-redux";
import Button from "./Button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAllDiscountApi } from "@/api/settings/discount";
import useAuth from "@/hooks/useAuth";
import { cashPaymentApi, getCourseWisePaymentApi, updateOrderServiceChargeApi, pointUseApi, completeUnpaidOrderApi, markCashOnDeliveryPaidApi, payByLinkApi, cancelOrderPayByLinkApi, orderTerminalPaymentApi, getSingleOrderApi } from "@/api/take-order/take-order";
import toast from "react-hot-toast";
import { setCourseWisePayToReduxState, setInitialCourseWisePayState, updateCourseWisePayLoadingState } from "@/redux/slice/take-order-slice/course-wise-pay";
import { setDiscountList } from "@/redux/slice/take-order-slice/discount-slice";
import { setOrderData } from "@/redux/slice/take-order-slice/order-slice";
import useSettings from "@/hooks/useSettings";
import useDebounce from "@/hooks/useDebounce";
import { FaUndo } from "react-icons/fa";
import { TableIcon } from "./Icons";
import { printCustomerCopy } from "@/lib/print-slip";
import LoyaltyDiscountPopup from "./LoyaltyDiscountPopup";
import PayByLinkPopup from "./PayByLinkPopup";
import CancelOrderPayByLinkDialog from "./CancelOrderPayByLinkDialog";
import PendingPayLinkConflictDialog from "@/components/common/PendingPayLinkConflictDialog";
import OrderCardPaymentConfirmDialog from "./OrderCardPaymentConfirmDialog";
import OrderCardPaymentResponseDialog from "./OrderCardPaymentResponseDialog";
import PaxPaymentTimeoutDialog from "./PaxPaymentTimeoutDialog";
import PusherHandler from "@/api/lib/pusher/PusherHandler";
import hasPermission from "@/hooks/hasPermission";
import getOrderAdvanceDetails from "@/lib/orderAdvanceUtils";
import getOrderTipsDetails from "@/lib/orderTipsUtils";
import getOrderVatDetails from "@/lib/orderVatUtils";
import { getOrderCustomerName, getOrderCustomerPhone } from "@/lib/utils";
import { getActiveRestaurantId } from "@/lib/idProvider";
const extractOrderData = (res) => {
    if (!res) return null;
    if (res.data?.order && (res.data.order.id || res.data.order.order_id)) {
        return res.data.order;
    }
    if (res.data && (res.data.id || res.data.order_id)) {
        return res.data;
    }
    if (res.order && (res.order.id || res.order.order_id)) {
        return res.order;
    }
    if (res.id || res.order_id) {
        return res;
    }
    return null;
};

const formatAmount = (val) => {
    if (val === null || val === undefined || val === "") return "0.00";
    const num = Number(val);
    return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

function BillingPage({setBillPage, setTipsPage, setSplitPaymentPage, setCourseWisePay}) {
    const [isTabletView, setIsTabletView] = useState(window.innerWidth <= 1200);
    const [isLoyaltyDiscountPopupOpen, setIsLoyaltyDiscountPopupOpen] = useState(false);
    const [isPayByLinkPopupOpen, setIsPayByLinkPopupOpen] = useState(false);
    const [isPayByLinkLoading, setIsPayByLinkLoading] = useState(false);
    const [isCancelPayByLinkOpen, setIsCancelPayByLinkOpen] = useState(false);
    const [isCancelPayByLinkLoading, setIsCancelPayByLinkLoading] = useState(false);
    const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
    const [conflictData, setConflictData] = useState(null);
    const [conflictTargetChannel, setConflictTargetChannel] = useState("email");
    const [isConflictLoading, setIsConflictLoading] = useState(false);
    const [isCardPaymentConfirmOpen, setIsCardPaymentConfirmOpen] = useState(false);
    const [isCardPaymentLoading, setIsCardPaymentLoading] = useState(false);
    const [isCardPaymentDialogOpen, setIsCardPaymentDialogOpen] = useState(false);
    const [cardPaymentResponseData, setCardPaymentResponseData] = useState(null);
    const [isPaxTimeoutDialogOpen, setIsPaxTimeoutDialogOpen] = useState(false);
    const [paxTimeoutData, setPaxTimeoutData] = useState(null);
    const { addEventHandler, removeEventHandler } = PusherHandler();
    const [isOrderActionPopupOpen, setIsOrderActionPopupOpen] = useState(false);
    const [isSubmittingOrderAction, setIsSubmittingOrderAction] = useState(false);
    const [completeUnpaidReason, setCompleteUnpaidReason] = useState("");
    const [paidAmount, setPaidAmount] = useState(0);
    const [redeemPoints, setRedeemPoints] = useState("");
    const [redeemValue, setRedeemValue] = useState("");
    const [isRedeemingPoints, setIsRedeemingPoints] = useState(false);
    const { orderData } = useSelector((state) => state.takeOrder.order); 
    const { discountList } = useSelector((state) => state.discount);
    const [serviceCharge, setServiceCharge] = useState(orderData?.service_charge || 0);
    const debouncedServiceCharge = useDebounce(serviceCharge, 700);
    const { user } = useAuth();
    const dispatch = useDispatch(); 
    const { settings } = useSettings(); 
    const restaurant_id = orderData?.restaurant_id || user?.restaurant_id || settings?.restaurant_id || getActiveRestaurantId();
    const POINT_TO_POUND_RATE = settings?.take_order?.reward_point_settings?.point_rate;
    const pointRate = Number(POINT_TO_POUND_RATE || 0);
    const maxRedeemValue = Number(orderData?.order_total || orderData?.total_price || 0);
    const maxRedeemPoints = pointRate > 0 ? Math.floor(maxRedeemValue / pointRate) : 0;

    useEffect(() => {
        const handleResize = () => {
            setIsTabletView(window.innerWidth <= 1200);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const updateRestricted = orderData?.payment_status === "paid" || orderData?.payment_status === "cash_on_delivery";
    const isDeliveryOrder = orderData?.order_type === "delivery";
    const paidToastShownForOrderRef = useRef(null);
    const appliedDiscounts = useMemo(() => orderData?.discounts || [], [orderData?.discounts]);
    const appliedLoyaltyDiscounts = useMemo(() => orderData?.loyalty_discounts || [], [orderData?.loyalty_discounts]);
    const discountAmount = Number(orderData?.discount_amount || 0);
    const loyaltyDiscountAmount = Number(orderData?.loyalty_discount_amount || 0);
    const totalSavings = Number(
        orderData?.total_savings !== undefined && orderData?.total_savings !== null
            ? orderData?.total_savings 
            : (discountAmount + loyaltyDiscountAmount + Number(orderData?.applied_spend_offer?.discount_amount || 0))
    ).toFixed(2);

    // Tips and Totals calculations
    const tipsInfo = getOrderTipsDetails(orderData);
    const tipsAmount = tipsInfo.totalTips;

    const isCardPayment =
        orderData?.payment?.payment_method === "card" ||
        orderData?.payment?.method === "card" ||
        orderData?.payments?.[0]?.method === "card";

    const backendReceivedAmount =
        orderData?.payment?.pay_amount !== undefined && orderData?.payment?.pay_amount !== null
            ? Number(orderData.payment.pay_amount)
            : orderData?.payments?.[0]?.received !== undefined && orderData?.payments?.[0]?.received !== null
            ? Number(orderData.payments[0].received)
            : orderData?.payment?.amount !== undefined && orderData?.payment?.amount !== null
            ? Number(orderData.payment.amount)
            : null;

    const effectivePaidAmount =
        backendReceivedAmount !== null
            ? backendReceivedAmount
            : Number(paidAmount || 0);

    const advanceInfo = getOrderAdvanceDetails(orderData);
    const advancePaidAmount = advanceInfo.isPaid ? advanceInfo.advanceAmount : 0;
    const hasAdvanceDeposit = Boolean(
        advancePaidAmount > 0 ||
        (advanceInfo?.hasAdvance && advanceInfo?.isPaid && advanceInfo?.advanceAmount > 0) ||
        (Number(orderData?.advance || 0) > 0 && orderData?.advance_payment?.status === "paid") ||
        (Boolean(orderData?.reservation?.is_deposit_paid) && Number(orderData?.reservation?.advance || 0) > 0)
    );
    const vatInfo = getOrderVatDetails(orderData);

    const finalBillAmount = Number(
        orderData?.total_price !== undefined && orderData?.total_price !== null
            ? orderData.total_price
            : orderData?.order_total || orderData?.subtotal || 0
    );

    const effectiveTotalToPay = Number(
        orderData?.total_price !== undefined && orderData?.total_price !== null
            ? orderData.total_price
            : finalBillAmount
    );

    const backendChangeAmount = isCardPayment
        ? 0
        : orderData?.payment?.change_amount !== undefined && orderData?.payment?.change_amount !== null
        ? Number(orderData.payment.change_amount)
        : orderData?.change_amount !== undefined && orderData?.change_amount !== null
        ? Number(orderData.change_amount)
        : (orderData?.payments?.[0]?.received !== undefined && orderData?.payments?.[0]?.received !== null && Number(orderData.payments[0].received) > Number(orderData.total_price || 0))
        ? Number(orderData.payments[0].received) - Number(orderData.total_price)
        : null;

    const changeAmount = isCardPayment
        ? 0
        : Number(
            backendChangeAmount !== null
                ? backendChangeAmount
                : (effectivePaidAmount > effectiveTotalToPay ? effectivePaidAmount - effectiveTotalToPay : 0)
        );

    const remainingDue = orderData?.payment_status === "paid"
        ? 0
        : Math.max(0, finalBillAmount - (orderData?.payment?.pay_amount ? Number(orderData.payment.pay_amount) : 0));

    const dispatchTime = orderData?.updated_at
        ? new Date(new Date(orderData.updated_at).getTime() + Number(orderData?.cooking_time || 0) * 60000).toLocaleTimeString()
        : "";
    const deliveryAddress = orderData?.delivery;
    const activeAddressText = deliveryAddress
        ? [
              deliveryAddress.door_no,
              deliveryAddress.address,
              deliveryAddress.address_line_1,
              deliveryAddress.city,
              deliveryAddress.state,
              deliveryAddress.postal_code,
          ]
              .filter(Boolean)
              .join(", ")
        : "N/A";

    const normalizeNumberInput = (value) => {
        if (value === "" || value === null || value === undefined) return "";
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : "";
    };

    const handleRedeemPointsChange = (value) => {
        const parsedPoints = normalizeNumberInput(value);
        if (parsedPoints === "" || parsedPoints < 0) {
            setRedeemPoints("");
            setRedeemValue("");
            return;
        }

        if (pointRate <= 0) {
            setRedeemPoints("");
            setRedeemValue("");
            return;
        }

        const wholePoints = Math.floor(parsedPoints);
        const boundedPoints = Math.min(wholePoints, maxRedeemPoints);
        const convertedValue = Number((boundedPoints * pointRate).toFixed(2));
        setRedeemPoints(boundedPoints);
        setRedeemValue(convertedValue);
    };

    const handleRedeemValueChange = (value) => {
        const parsedValue = normalizeNumberInput(value);
        if (parsedValue === "" || parsedValue < 0) {
            setRedeemPoints("");
            setRedeemValue("");
            return;
        }

        const inputValue = Number(parsedValue);
        setRedeemValue(Number(inputValue.toFixed(2)));

        if (pointRate <= 0) {
            setRedeemPoints("");
            return;
        }

        // Keep typed value unchanged; for conversion, cap by order total and round up points.
        const conversionBaseValue = Math.min(inputValue, maxRedeemValue);
        const convertedPoints = Math.ceil(conversionBaseValue / pointRate);
        setRedeemPoints(convertedPoints > 0 ? convertedPoints : "");
    };

    const pointsUseDisabled =
        updateRestricted ||
        isRedeemingPoints ||
        pointRate <= 0 ||
        redeemPoints === "" ||
        redeemValue === "" ||
        !Number.isInteger(Number(redeemPoints)) ||
        Number(redeemPoints) <= 0 ||
        Number(redeemValue) <= 0 ||
        Number(redeemValue) > maxRedeemValue;

    const redeemPointsToServer = async () => {

        if (orderData?.customer?.id == null) {
            return toast.error("Customer information is missing. Cannot redeem points.");
        }

        if (updateRestricted) return toast.error("Order is Paid. No changes allowed");
        if (pointRate <= 0) return toast.error("Point conversion rate is not configured.");
        if (redeemPoints === "" || redeemValue === "" || Number(redeemPoints) <= 0 || Number(redeemValue) <= 0) {
            return toast.error("Please enter points or value to redeem.");
        }
        if (!Number.isInteger(Number(redeemPoints))) {
            return toast.error("Points must be a whole number.");
        }
        if (Number(redeemValue) > maxRedeemValue) {
            return toast.error(`Maximum redeem value is £ ${maxRedeemValue.toFixed(2)}.`);
        }

        setIsRedeemingPoints(true);
        try {
            const response = await pointUseApi(orderData?.id, {
                customer_id: orderData?.customer?.id,
                points: Number(redeemPoints),
                points_value: Number(redeemValue),
            });

            if (response?.status === "success") {
                toast.success("Points redeemed successfully");
                const updatedOrder = extractOrderData(response);
                dispatch(setOrderData(updatedOrder || response?.data || response?.data?.order || orderData));
                setRedeemPoints("");
                setRedeemValue("");
            } else {
                toast.error(response?.message || "Failed to redeem points");
            }
        } catch (error) {
            console.error("Failed to redeem points:", error);
            toast.error("Failed to redeem points. Please try again.");
        } finally {
            setIsRedeemingPoints(false);
        }
    };

    const handleOpenOrderActionPopup = () => {
        if (updateRestricted) return toast.error("Order is Paid. No changes allowed");
        setCompleteUnpaidReason("");
        setIsOrderActionPopupOpen(true);
    };

    const confirmOrderAction = async () => {
        if (updateRestricted) return toast.error("Order is Paid. No changes allowed");
        const trimmedReason = completeUnpaidReason.trim();

        if (!isDeliveryOrder && !trimmedReason) {
            return toast.error("Please enter a reason before completing this order unpaid.");
        }

        setIsSubmittingOrderAction(true);
        try {
            const response = isDeliveryOrder
                ? await markCashOnDeliveryPaidApi(orderData?.id)
                : await completeUnpaidOrderApi(orderData?.id, {
                    complete_unpaid_reason: trimmedReason,
                });

            if (response?.status === "success") {
                const updatedOrder = extractOrderData(response);
                if (updatedOrder) {
                    dispatch(setOrderData(updatedOrder));
                }
                toast.success(isDeliveryOrder ? "Order marked as cash on delivery" : "Order completed without payment");
                setIsOrderActionPopupOpen(false);
            } else {
                toast.error(response?.message || "Failed to update order status");
            }
        } catch (error) {
            console.error("Failed to update order status:", error);
            toast.error("Failed to update order status. Please try again.");
        } finally {
            setIsSubmittingOrderAction(false);
        }
    };

    const handleOpenPayByLinkPopup = () => {
        if (updateRestricted) return toast.error("Order is Paid. No changes allowed");
        setIsPayByLinkPopupOpen(true);
    };

    const handlePayByLinkSubmit = async (channel) => {
        if (!channel) {
            return toast.error("Please select a channel (SMS or Email)");
        }

        try {
            const response = await payByLinkApi(orderData?.id, {
                channel: channel
            });

            if (response?.status === "success") {
                // Update order data if returned
                const updatedOrder = extractOrderData(response);
                if (updatedOrder) {
                    dispatch(setOrderData(updatedOrder));
                }
                // Automatically open popup to allow user to manage / cancel the active payment link
                setIsCancelPayByLinkOpen(true);
                return response;
            } else if (response?.data?.error_code === "pay_link_already_pending") {
                // Conflict detected: an active pay-by-link already exists
                setConflictData(response.data.existing_pay_link);
                setConflictTargetChannel(channel);
                setIsPayByLinkPopupOpen(false);
                setIsConflictDialogOpen(true);
                return response;
            } else {
                toast.error(response?.message || "Failed to send payment link");
                return response;
            }
        } catch (error) {
            console.error("Failed to send payment link:", error);
            throw error;
        }
    };

    const handleConflictCancelAndResend = async () => {
        if (!orderData?.id) return;
        setIsConflictLoading(true);
        try {
            // 1. Cancel existing link
            const cancelRes = await cancelOrderPayByLinkApi(orderData.id);
            if (cancelRes?.status === "success") {
                toast.success("Previous payment link cancelled. Sending new link...");
                // 2. Resend new link
                const resendRes = await payByLinkApi(orderData.id, {
                    channel: conflictTargetChannel
                });
                if (resendRes?.status === "success") {
                    toast.success(`New payment link sent via ${conflictTargetChannel.toUpperCase()}`);
                    setIsConflictDialogOpen(false);
                    setConflictData(null);
                    const updatedOrder = extractOrderData(resendRes);
                    if (updatedOrder) {
                        dispatch(setOrderData(updatedOrder));
                    }
                    setIsCancelPayByLinkOpen(true);
                } else {
                    toast.error(resendRes?.message || "Failed to send new payment link");
                }
            } else {
                toast.error(cancelRes?.message || "Failed to cancel previous payment link");
            }
        } catch (error) {
            console.error("Failed in cancel and resend:", error);
            toast.error(error?.message || "Something went wrong.");
        } finally {
            setIsConflictLoading(false);
        }
    };

    const handleConflictCancelOnly = async () => {
        if (!orderData?.id) return;
        setIsConflictLoading(true);
        try {
            const cancelRes = await cancelOrderPayByLinkApi(orderData.id);
            if (cancelRes?.status === "success") {
                toast.success(cancelRes?.message || "Previous payment link cancelled.");
                setIsConflictDialogOpen(false);
                setConflictData(null);
                const updatedOrder = extractOrderData(cancelRes);
                if (updatedOrder) {
                    dispatch(setOrderData(updatedOrder));
                }
            } else {
                toast.error(cancelRes?.message || "Failed to cancel previous payment link");
            }
        } catch (error) {
            console.error("Failed to cancel payment link:", error);
            toast.error(error?.message || "Something went wrong.");
        } finally {
            setIsConflictLoading(false);
        }
    };

    const handleCancelOrderPayByLink = async () => {
        if (!orderData?.id) return;

        setIsCancelPayByLinkLoading(true);
        try {
            const response = await cancelOrderPayByLinkApi(orderData.id);
            if (response?.status === "success") {
                toast.success(response?.message || "Payment link cancelled.");
                setIsCancelPayByLinkOpen(false);
                const updatedOrder = extractOrderData(response);
                if (updatedOrder) {
                    dispatch(setOrderData(updatedOrder));
                }
            } else {
                toast.error(response?.message || "Failed to cancel payment link.");
            }
        } catch (error) {
            console.error("Failed to cancel payment link:", error);
            toast.error(error?.message || "Something went wrong.");
        } finally {
            setIsCancelPayByLinkLoading(false);
        }
    };

    const isInsufficientPaidAmount = !(Number(paidAmount) >= remainingDue);
    // helper used for cash payment button state
    const cashDisabled = orderData?.payment_status === "paid" || isInsufficientPaidAmount;
    const cashButtonTitle = orderData?.payment_status === "paid"
        ? "No changes allowed"
        : isInsufficientPaidAmount
            ? "Paid amount must be greater than or equal to remaining due amount"
            : "Cash payment";




    function setCourseWisePayToRedux() {
        if (updateRestricted) return toast.error("Order is Paid. No changes allowed");
        if (hasAdvanceDeposit) {
            return toast.error("Course Wise Pay is not available for orders with an advance deposit.");
        }
        
        setCourseWisePay(true);

        dispatch(updateCourseWisePayLoadingState(true));
        getCourseWisePaymentApi(restaurant_id, orderData.id).then((res) => { 
            
            dispatch(updateCourseWisePayLoadingState(false));

            if (res.status === "success" && res.data) {
                const data = res.data?.course_wise_payment;
                dispatch(setCourseWisePayToReduxState(data));
                console.log("Fetched Course Wise Pay data:", data);
            }else{
                dispatch(setInitialCourseWisePayState());
                if(res?.message != "Course-wise payment not found"){
                    console.error("Failed to fetch Course Wise Pay data:", res);
                    toast.error( res?.message || "Failed to fetch Course Wise Pay data. Please try again.");
                }
            }
        }).catch((err) => {
            console.error("Error fetching Course Wise Pay data:", err);
            toast.error("Failed to fetch Course Wise Pay data. Please try again.");
        });
    }


    useEffect(()=>{ 
        setServiceCharge(orderData?.service_charge || 0);
        // setLoyaltyCode(orderData?.loyalty_discount_short_code || "");
    }, [orderData])

    useEffect(() => {
        if (!restaurant_id) return;

        getAllDiscountApi(restaurant_id, {
            per_page: 50,
            status: "active",
        }).then((response) => {
            if (response?.status === "success") {
                const discounts = response?.data?.discounts || response?.data || [];
                dispatch(setDiscountList(discounts));
            }
        }).catch((err) => {
            console.error("Failed to fetch discounts in billingPage:", err);
        });
    }, [
        restaurant_id,
        dispatch,
    ]);

    const updateServiceCharge = useCallback((amount) => {
        if (updateRestricted) return toast.error("No changes allowed");
        console.log("Updating service charge for order:", orderData?.id, "to amount:", amount);
        updateOrderServiceChargeApi(orderData?.id, { amount }).then((data) => { 
            if (data.status === "success") {
                console.log("Service Charge Updated --->", data);
                toast.success("Service Charge Updated Successfully");
                dispatch(setOrderData(data?.data));
            } else {
                console.log("Error Updating Service Charge --->", data);
                toast.error(data?.message || "Error Updating Service Charge");
            }
        });
    }, [dispatch, updateRestricted, orderData?.id]);

    // Send service charge to backend only after the user stops typing (debounced)
    useEffect(() => {
        const parsed = parseFloat(debouncedServiceCharge);
        const current = orderData?.service_charge ?? 0;

        // Only send if parsed is a valid number and different from current backend value
        if (debouncedServiceCharge !== "" && !isNaN(parsed) && parsed !== current) {
            updateServiceCharge(parsed);
            console.log("Updating service charge to:", parsed);
        }
    }, [debouncedServiceCharge, orderData?.service_charge, updateServiceCharge]);



    function cashPayment(){
        if (orderData?.payment_status === "paid") return toast.error("No changes allowed");
        cashPaymentApi(orderData?.id, { pay_amount: paidAmount, payment_method: "cash" }).then((data)=>{
            if(data.status == "success"){
                console.log("Cash Payment Successful --->", data);
                toast.success("Cash Payment Successful");
                if (data?.data?.payment?.pay_amount) {
                    setPaidAmount(data.data.payment.pay_amount);
                } else if (data?.data?.payments?.[0]?.received) {
                    setPaidAmount(data.data.payments[0].received);
                }
                dispatch( setOrderData (data?.data));
            }else{
                console.log("Error in Cash Payment --->", data);
                toast.error(data?.message || "Error in Cash Payment");
            }
        }).catch((err) => {
            console.error("Error in Cash Payment --->", err);
            toast.error("Error in Cash Payment. Please try again.");
        });
    }

    const handleTerminalCardPayment = async () => {
        if (updateRestricted) return toast.error("Order is Paid. No changes allowed");
        if (!orderData?.id) return toast.error("Order ID is missing");

        setIsCardPaymentLoading(true);
        try {
            const response = await orderTerminalPaymentApi(orderData.id);
            console.log("Terminal Payment Response --->", response);
            if (response?.status === "success") {
                toast.success(response?.message || "Terminal payment dispatched to the device.");
                setIsCardPaymentConfirmOpen(false);
                setCardPaymentResponseData(response);
                setIsCardPaymentDialogOpen(true);
                // If updated order data is returned, sync with redux
                if (response?.data?.order) {
                    dispatch(setOrderData(response.data.order));
                }
            } else {
                toast.error(response?.message || "Failed to dispatch card payment to terminal");
            }
        } catch (error) {
            console.error("Error in terminal card payment:", error);
            toast.error("Error dispatching payment to terminal. Please try again.");
        } finally {
            setIsCardPaymentLoading(false);
        }
    };

    const handleTerminalPaymentCompleted = async (response) => {
        if (response?.data?.order) {
            dispatch(setOrderData(response.data.order));
        } else if (orderData?.id) {
            try {
                const freshOrder = await getSingleOrderApi(orderData.id);
                if (freshOrder?.data) {
                    dispatch(setOrderData(freshOrder.data));
                }
            } catch (e) {
                console.error("Error refreshing order after terminal payment:", e);
            }
        }
    };

    const handleTerminalPaymentCancelled = async (response) => {
        if (response?.data?.order) {
            dispatch(setOrderData(response.data.order));
        } else if (orderData?.id) {
            try {
                const freshOrder = await getSingleOrderApi(orderData.id);
                if (freshOrder?.data) {
                    dispatch(setOrderData(freshOrder.data));
                }
            } catch (e) {
                console.error("Error refreshing order after terminal payment cancellation:", e);
            }
        }
    };

    // Pusher real-time listener for order updates and PAX payment timeouts
    useEffect(() => {
        const handleOrderUpdated = (data) => {
            console.log("Billing page Pusher event [order-updated]:", data);
            if (!data || Number(data.order_id) !== Number(orderData?.id)) return;

            if (
                data.type === "order_payment_timeout" ||
                data.type === "terminal_payment_timeout" ||
                data.type === "pre_order_advance_timeout"
            ) {
                console.log("PAX terminal payment timeout received for order:", data.order_id);
                // 1. Close all open PAX payment popups
                setIsCardPaymentDialogOpen(false);
                setIsCardPaymentConfirmOpen(false);

                // 2. Update Redux order if returned
                if (orderData) {
                    dispatch(setOrderData({
                        ...orderData,
                        payment_status: data.payment_status || "unpaid",
                        stage: data.stage || orderData.stage,
                        kitchen_status: data.kitchen_status || orderData.kitchen_status,
                        cooking_time: data.cooking_time ?? orderData.cooking_time,
                        cooking_time_set_at: data.cooking_time_set_at ?? orderData.cooking_time_set_at,
                        cooking_start_time: data.cooking_start_time ?? orderData.cooking_start_time,
                        cooking_end_time: data.cooking_end_time ?? orderData.cooking_end_time,
                        payment: data.payment || null,
                    }));
                }

                // 3. Open the PAX timeout dialog
                setPaxTimeoutData(data);
                setIsPaxTimeoutDialogOpen(true);
                toast.error("PAX terminal payment timed out.");
            } else if (
                data.type === "order_payment_cancelled" ||
                data.type === "order_refund_cancelled" ||
                data.type === "terminal_payment_cancelled" ||
                data.type === "order_payment_failed" ||
                data.type === "order_refund_failed" ||
                data.status === "cancelled"
            ) {
                // Close all open PAX dialogs on cancel
                setIsCardPaymentDialogOpen(false);
                setIsCardPaymentConfirmOpen(false);
                setIsPaxTimeoutDialogOpen(false);
                setPaxTimeoutData(null);
                toast.error(data?.error_message || data?.message || "PAX terminal payment cancelled.");
            } else if (
                data.payment_status === "paid" ||
                data.type === "order_payment_completed" ||
                data.type === "terminal_payment_approved" ||
                data.payment
            ) {
                // Close any open PAX card dialogs
                setIsCardPaymentDialogOpen(false);
                setIsCardPaymentConfirmOpen(false);
                setIsPaxTimeoutDialogOpen(false);
                setPaxTimeoutData(null);

                const existingPayment = orderData?.payment || {};
                const incomingPayment = data.payment
                    ? { ...existingPayment, ...data.payment }
                    : existingPayment;
                const incomingTipsAmount = Number(
                    incomingPayment?.tips_amount ??
                    incomingPayment?.tips ??
                    data.tips_amount ??
                    data.tips ??
                    orderData?.tips_amount ??
                    0
                );
                const incomingTips =
                    incomingPayment?.tips !== undefined && incomingPayment?.tips !== null
                        ? incomingPayment.tips
                        : data.tips !== undefined && data.tips !== null
                        ? data.tips
                        : orderData?.tips ?? "0";
                const incomingTipType =
                    incomingPayment?.tip_type || data.tip_type || orderData?.tip_type || "fixed";
                const incomingTotalPrice =
                    data.total_price !== undefined && data.total_price !== null
                        ? data.total_price
                        : incomingPayment?.amount !== undefined && incomingPayment?.amount !== null
                        ? incomingPayment.amount
                        : incomingPayment?.pay_amount !== undefined && incomingPayment?.pay_amount !== null
                        ? incomingPayment.pay_amount
                        : orderData?.total_price;

                if (orderData) {
                    dispatch(setOrderData({
                        ...orderData,
                        payment_status: data.payment_status || orderData.payment_status || "paid",
                        stage: data.stage || orderData.stage,
                        kitchen_status: data.kitchen_status || orderData.kitchen_status,
                        cooking_time: data.cooking_time ?? orderData.cooking_time,
                        cooking_time_set_at: data.cooking_time_set_at ?? orderData.cooking_time_set_at,
                        cooking_start_time: data.cooking_start_time ?? orderData.cooking_start_time,
                        cooking_end_time: data.cooking_end_time ?? orderData.cooking_end_time,
                        payment: incomingPayment,
                        tips: incomingTips,
                        tips_amount: incomingTipsAmount,
                        total_tips_amount: data.total_tips_amount ?? incomingTipsAmount,
                        tip_type: incomingTipType,
                        tips_mode: data.tips_mode || orderData?.tips_mode,
                        tips_breakdown: data.tips_breakdown || orderData?.tips_breakdown,
                        total_price: incomingTotalPrice,
                    }));
                }
                const isCardMethod =
                    data.type === "terminal_payment_approved" ||
                    incomingPayment?.payment_method === "card" ||
                    incomingPayment?.method === "card" ||
                    data.payment_method === "card";

                if (isCardMethod) {
                    toast.success("Card payment completed successfully!");
                }
            }
        };

        const unsubscribe = addEventHandler("order-updated", handleOrderUpdated);
        return () => {
            if (typeof unsubscribe === "function") {
                unsubscribe();
            } else {
                removeEventHandler("order-updated", handleOrderUpdated);
            }
        };
    }, [addEventHandler, removeEventHandler, orderData, dispatch]);

    const handleRetryTerminalPaymentFromTimeout = () => {
        setIsPaxTimeoutDialogOpen(false);
        handleTerminalCardPayment();
    };

    console.log("Rendering BillingPage - updateRestricted:", updateRestricted);

    useEffect(() => {
        if (updateRestricted && orderData?.id && paidToastShownForOrderRef.current !== orderData.id) {
            paidToastShownForOrderRef.current = orderData.id;
            toast.success("Order is Paid — no further changes are allowed");
            // setLastNotifiedPaidOrderId(orderData.id);
        }
    }, [updateRestricted, orderData?.id]);
    

    return(
        <>
        <div className={`${isTabletView ? "w-[680px] h-[690px] max-h-[96vh]" : "w-[1120px] h-[850px] max-h-[96vh]"} ${isTabletView ? "p-3" : "p-4"} bg-color2 border-2 border-color3 rounded-md flex flex-col`}>
 
            <h1 className={`${isTabletView ? "h-[24px] -mt-1 text-lg mb-1.5" : "h-[28px] -mt-2 text-2xl mb-2"} font-bold uppercase text-color7 text-center`}>{ updateRestricted ? ` ${orderData?.payment_status === "paid"? "Paid" : "Cash on Delivery"}` : "" }</h1>
            <div className="w-full flex-1 min-h-0 flex gap-3">
                <div className={ `${isTabletView ? "w-[60%]" : "w-full"} h-full flex flex-col min-h-0`}>

                    <div className={`w-full ${isTabletView ? "min-h-[150px]" : "min-h-[190px]"} bg-color3 ${isTabletView ? "p-1.5" : "p-3"} flex ${isTabletView ? "flex-col gap-1" : "gap-2 justify-between"} rounded-sm ${isTabletView ? "py-2" : "py-3"}`}>
                        <div className={`flex ${isTabletView ? "flex-col gap-1.5 text-xs" : "flex-col gap-3"}`}>
                            <div>
                                <span className={`font-semibold ${isTabletView ? "text-xs" : ""}`}>Order ID: </span>
                                <span>{orderData?.id}</span>
                            </div>
                            <div>
                                <span className={`font-semibold ${isTabletView ? "text-xs" : ""}`}>Customer: </span>
                                <span className={`truncate ${isTabletView ? "text-xs max-w-[150px]" : ""}`}>{getOrderCustomerName(orderData, "Unknown")}</span>
                            </div>
                            <div>
                                <span className={`font-semibold ${isTabletView ? "text-xs" : ""}`}>Phone: </span>
                                <span>{getOrderCustomerPhone(orderData, "-")}</span>
                            </div>
                            {
                                settings?.take_order?.reward_point_settings?.is_reward_points_enabled && (
                                    <>
                                        <div>
                                            <span className={`font-semibold ${isTabletView ? "text-xs" : ""}`}>Total Point: </span>
                                            <span>{orderData?.customer?.points || 0}</span>
                                        </div>
                                        <div>
                                            <span className={`font-semibold ${isTabletView ? "text-xs" : ""}`}>Points Value: </span>
                                            <span>£ {formatAmount(orderData?.customer?.points_value)}</span>
                                        </div>
                                    </>
                                )
                            }
                        </div>
                        <div className={` w-[55%] flex ${isTabletView ? "flex-col gap-1.5 text-xs" : "flex-col gap-3"}`}>
                            <div className="flex items-center gap-2">
                                <span className={`text-color6 font-semibold ${isTabletView ? "text-xs" : ""}`}>{(orderData?.order_type == "pickup")? "Pickup" : (orderData?.order_type == "delivery")? "Delivery" :  (orderData?.order_type == "in_house")? "In House" :  (orderData?.order_type == "table")? "Table" : "" }</span>
                                {(orderData?.reservation?.table?.name || advanceInfo.tableName) && (
                                    <div className="flex items-center gap-1">
                                        <span><TableIcon className="w-4 h-4 stroke-color6" /></span>
                                        <span className="text-color6">{advanceInfo.tableName || orderData?.reservation?.table?.name}</span>
                                    </div>
                                )}
                            </div>
                            {
                                !isTabletView &&
                                <div>
                                    <span className=" font-semibold">Address: </span>
                                    <span>{activeAddressText}</span>
                                </div>
                            }
                            <div>
                                <span className=" font-semibold">Date: </span>
                                <span>{new Date(orderData?.created_at).toLocaleDateString()}</span>
                            </div>
                            <div>
                                {orderData?.updated_at && !isTabletView && (
                                    <div className="flex items-center gap-1">
                                        <span className=" font-semibold">Accepted </span>
                                        <span className="text-color5">{orderData?.updated_at && new Date(orderData?.updated_at ).toLocaleTimeString()}</span>
                                    </div>
                                )}
                            </div>
                            <div>
                                {orderData?.updated_at && !isTabletView && (
                                    <div className="flex items-center gap-1">
                                        <span className=" font-semibold">Dispatch </span>
                                        <span className="text-color5">{dispatchTime}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
 
                    {
                        settings?.take_order?.service_charge_setting?.is_service_charge_enabled && (
                            <div className={`w-full ${isTabletView ? "mt-1.5" : "mt-4"} flex ${isTabletView ? "gap-1.5 text-xs" : "gap-4"} items-center justify-between`}>
                                <div className={`${isTabletView ? "w-[35%]" : "w-[40%]"} shrink-0`}>
                                    <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : ""}`}>Service Charge (%)</span>
                                </div>
                                <div className={`w-full flex items-center justify-center ${isTabletView ? "gap-1" : "gap-2"}`}>
                                    <input type="number" onChange={(e)=> setServiceCharge(e.target.value)} value={serviceCharge} name="serviceCharge" id="serviceCharge" disabled={updateRestricted} className={`w-full grow py-2 px-2 bg-color2 border-2 border-color3 outline-none rounded-md ${isTabletView ? "text-xs py-1.5 px-1.5" : ""}`}/>
                                    <button title=" Reset service charge" onClick={() => setServiceCharge(settings?.take_order?.service_charge_setting?.amount)} disabled={updateRestricted} className={`${isTabletView ? "py-2 px-3 text-sm" : "py-[14px] px-7"} bg-color3 rounded-md text-color6 ${updateRestricted ? 'opacity-50 cursor-not-allowed' : ''}`}> <FaUndo /> </button>
                                </div>
                                
                            </div>
                        )
                    }  
                    <div className="mt-auto">
                        {
                            settings?.take_order?.reward_point_settings?.is_reward_points_enabled && (
                                <>
                                    <div className={`w-full ${isTabletView ? "mt-1.5" : "mt-4"} flex ${isTabletView ? "gap-1.5 text-xs" : "gap-4"} items-center justify-between ${isTabletView ? "pb-1.5" : "pb-4"} `}>
                                        <div className={`${isTabletView ? "w-[25%]" : "w-[30%]"} shrink-0`}>
                                            <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : ""}`}>Points</span>
                                        </div>
                                        <input
                                            type="number"
                                            name="points"
                                            value={redeemPoints}
                                            onChange={(e) => handleRedeemPointsChange(e.target.value)}
                                            id="points"
                                            min={0}
                                            step={1}
                                            max={maxRedeemPoints}
                                            disabled={updateRestricted}
                                            className={`w-full grow py-2 px-2 bg-color2 border-2 border-color3 outline-none rounded-md ${isTabletView ? "text-xs py-1.5 px-1.5" : ""}`}
                                        />
                                        <input
                                            type="number"
                                            name="pointValue"
                                            value={redeemValue}
                                            onChange={(e) => handleRedeemValueChange(e.target.value)}
                                            id="pointValue"
                                            min={0}
                                            step={0.01}
                                            disabled={updateRestricted}
                                            className={`w-full grow py-2 px-2 bg-color2 border-2 border-color3 outline-none rounded-md ${isTabletView ? "text-xs py-1.5 px-1.5" : ""}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={redeemPointsToServer}
                                            disabled={pointsUseDisabled}
                                            className={`py-2 px-4 bg-color7 rounded-md ${pointsUseDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                                        >
                                            {isRedeemingPoints ? "Using..." : "Use"}
                                        </button>
                                    </div>
                                    <p className={`${isTabletView ? "text-[10px]" : "text-xs"} text-color4 -mt-2`}>Points are whole numbers only. Max redeem value is £ {maxRedeemValue.toFixed(2)}. Value-to-point conversion uses ceil for fractional points and keeps your typed value unchanged.</p>
                                </>
                            )
                        }

                        <div className={`w-full ${isTabletView ? "mt-0.5" : "mt-1"} flex ${isTabletView ? "gap-1.5 text-xs" : "gap-4"} items-center justify-between ${isTabletView ? "pb-1.5" : "pb-4"} `}>
                            <div className={`${isTabletView ? "w-[25%]" : "w-[30%]"} shrink-0`}>
                                <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : ""}`}>Cash Paid</span>
                            </div>
                            <input
                                type={orderData?.payment_status === "paid" ? "text" : "number"}
                                step="0.01"
                                name="voucher"
                                value={
                                    orderData?.payment_status === "paid"
                                        ? formatAmount(orderData?.payment?.pay_amount ?? orderData?.payments?.[0]?.received ?? orderData?.payment?.amount ?? paidAmount)
                                        : paidAmount
                                }
                                onChange={(e) => setPaidAmount(e.target.value)}
                                id="voucher"
                                disabled={orderData?.payment_status === "paid"}
                                className={`w-full grow py-2 px-2 bg-color2 border-2 border-color3 outline-none rounded-md ${isTabletView ? "text-xs py-1.5 px-1.5" : ""}`}
                            />
                        </div>
                        <div className={`w-full ${isTabletView ? "mt-0.5" : "mt-1"} flex ${isTabletView ? "gap-1.5 text-xs" : "gap-4"} items-center justify-between ${isTabletView ? "pb-1.5" : "pb-4"} border-b border-dashed border-color4`}>
                            <div className={`${isTabletView ? "w-[25%]" : "w-[30%]"} shrink-0`}>
                                <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : ""}`}>Change</span>
                            </div>
                            <input
                                type="text"
                                name="voucher"
                                value={formatAmount(changeAmount)}
                                id="voucher"
                                disabled={true}
                                className={`w-full grow py-2 px-2 bg-color2 border-2 border-color3 outline-none rounded-md ${isTabletView ? "text-xs py-1.5 px-1.5" : ""}`}
                            />
                        </div>
                        <div className={`w-full ${isTabletView ? "mt-2 gap-1.5" : "mt-6 gap-2"} flex flex-col justify-between`}>
                            <div className={`flex ${isTabletView ? "gap-1.5" : "gap-2"}`}>
                                <button onClick={()=> { if(!hasPermission("take_orders.take_order.pay_by_link")){ toast.error("You do not have permission to use this feature."); } else { handleOpenPayByLinkPopup(); } }} className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} font-bold rounded-md flex flex-col items-center justify-center gap-0.5 ${hasPermission("take_orders.take_order.pay_by_link") ? "bg-blue-500" : "bg-gray-500"} ${updateRestricted ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={updateRestricted}>
                                    <span>Pay by Link</span>
                                </button>
                                <button onClick={cashPayment} title={cashButtonTitle} className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} font-bold rounded-md flex flex-col items-center justify-center gap-0.5 bg-color7 ${cashDisabled ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={cashDisabled}>
                                    <span>Cash</span>
                                </button>
                                <button onClick={()=> { if(!hasPermission("take_orders.take_order.card_pay")){ toast.error("You do not have permission to use this feature."); } else { if (updateRestricted) return toast.error("Order is Paid. No changes allowed"); setIsCardPaymentConfirmOpen(true); } }} className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} font-bold rounded-md flex flex-col items-center justify-center gap-0.5 ${hasPermission("take_orders.take_order.card_pay") ? "bg-cyan-500" : "bg-gray-500"} ${updateRestricted ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={updateRestricted}>
                                    <span>Card</span>
                                </button>
                            </div>
                            <div className={`flex ${isTabletView ? "gap-1.5" : "gap-2"}`}>
                                <button
                                    onClick={() => {
                                        if (hasAdvanceDeposit) {
                                            toast.error("Course Wise Pay is not available for orders with an advance deposit.");
                                            return;
                                        }
                                        if (updateRestricted) {
                                            toast.error("Order is Paid. No changes allowed");
                                            return;
                                        }
                                        if (!hasPermission("take_orders.take_order.course_wise_pay")) {
                                            toast.error("You do not have permission to use this feature.");
                                            return;
                                        }
                                        setCourseWisePayToRedux();
                                    }}
                                    disabled={updateRestricted || hasAdvanceDeposit}
                                    title={
                                        hasAdvanceDeposit
                                            ? "Course Wise Pay is not available for orders with an advance deposit"
                                            : updateRestricted
                                                ? "Order is Paid. No changes allowed"
                                                : "Course Wise Pay"
                                    }
                                    className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} font-bold rounded-md flex flex-col items-center justify-center gap-0.5 ${
                                        hasPermission("take_orders.take_order.course_wise_pay") && !hasAdvanceDeposit && !updateRestricted
                                            ? "bg-green-500"
                                            : "bg-gray-500 opacity-50 cursor-not-allowed"
                                    }`}
                                >
                                    <span>Course Wise Pay</span>
                                </button>
                                <button
                                    onClick={() => {
                                        if (hasAdvanceDeposit) {
                                            toast.error("Split payment is not available for orders with an advance deposit.");
                                            return;
                                        }
                                        if (updateRestricted) {
                                            toast.error("Order is Paid. No changes allowed");
                                            return;
                                        }
                                        if (!hasPermission("take_orders.take_order.payment_split")) {
                                            toast.error("You do not have permission to use this feature.");
                                            return;
                                        }
                                        setSplitPaymentPage(true);
                                    }}
                                    disabled={updateRestricted || hasAdvanceDeposit}
                                    title={
                                        hasAdvanceDeposit
                                            ? "Split payment is not available for orders with an advance deposit"
                                            : updateRestricted
                                                ? "Order is Paid. No changes allowed"
                                                : "Split"
                                    }
                                    className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} font-bold rounded-md flex flex-col items-center justify-center gap-0.5 ${
                                        hasPermission("take_orders.take_order.payment_split") && !hasAdvanceDeposit && !updateRestricted
                                            ? "bg-emerald-500"
                                            : "bg-gray-500 opacity-50 cursor-not-allowed"
                                    }`}
                                >
                                    <span>Split </span>
                                </button>
                                <button onClick={()=> setTipsPage(true)} className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} font-bold rounded-md flex flex-col items-center justify-center gap-0.5 bg-teal-500 ${updateRestricted ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={updateRestricted}>
                                    <span>Add Tips</span>
                                </button> 
                            </div>
                        </div> 
                        <div className={`w-full ${isTabletView ? "mt-1.5 gap-1.5" : "mt-2 gap-2"} flex flex-col justify-between`}>
                            <div className={`flex ${isTabletView ? "gap-1.5" : "gap-2"}`}>
                                <button onClick={() =>{ if(!hasPermission("take_orders.take_order.loyalty")){ toast.error("You do not have permission to use this feature."); } else { setIsLoyaltyDiscountPopupOpen(true); } }} disabled={updateRestricted} className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} font-bold rounded-md flex flex-col items-center justify-center gap-0.5 ${hasPermission("take_orders.take_order.loyalty") ? "bg-yellow-500" : "bg-gray-500"} ${updateRestricted ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <span>Loyalty/Discount</span>
                                </button>
                                <button onClick={() => printCustomerCopy(orderData?.id)} className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} font-bold rounded-md flex flex-col items-center justify-center gap-0.5 bg-amber-500`}>
                                    <span>Customer Copy</span>
                                </button>
                                {
                                orderData?.order_type == "delivery"?
                                    <button onClick={()=> { if(!hasPermission("take_orders.take_order.cash_on_delivery")){ toast.error("You do not have permission to use this feature."); } else { handleOpenOrderActionPopup(); } }} disabled={updateRestricted} className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} ${hasPermission("take_orders.take_order.cash_on_delivery") ? "bg-orange-500" : "bg-gray-500"} font-bold rounded-md flex flex-col items-center justify-center gap-0.5 ${updateRestricted ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        <span>Cash On Delivery</span>
                                    </button>:
                                    <button onClick={()=> { if(!hasPermission("take_orders.take_order.unpaid")){ toast.error("You do not have permission to use this feature."); } else { handleOpenOrderActionPopup(); } }} disabled={updateRestricted} className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} ${hasPermission("take_orders.take_order.unpaid") ? "bg-orange-500" : "bg-gray-500"} font-bold rounded-md flex flex-col items-center justify-center gap-0.5 ${updateRestricted ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        <span>Unpaid</span>
                                    </button>
                                }
                                {/* <button className={` w-full p-2 font-bold rounded-md flex flex-col items-center justify-center gap-1 bg-color6 ${updateRestricted ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={updateRestricted}>
                                    <span>Make as Complete </span>
                                </button> */}
                            </div>
                            <div className={`flex ${isTabletView ? "gap-1.5" : "gap-2"}`}>
                                <button onClick={()=> setBillPage(false)} className={`w-full ${isTabletView ? "p-1.5 py-2 text-xs" : "p-2"} bg-red-500 font-bold rounded-md flex flex-col items-center justify-center gap-0.5`}>
                                    <span>Close</span>
                                </button>
                            </div>
                            {/* <div className=" flex gap-2">
                                <button onClick={()=> setBillPage(false)} className=" w-full p-2 bg-red-500 font-bold rounded-md flex flex-col items-center justify-center gap-1">
                                    <span>Close</span>
                                </button>
                                <button className={` w-full p-2 font-bold rounded-md flex flex-col items-center justify-center gap-1 bg-green-500 ${updateRestricted ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={updateRestricted}>
                                    <span>Print & Complete </span>
                                </button> 
                            </div> */}
                        </div>
                    </div>
                </div>
                <div className={`${isTabletView ? "w-[40%]" : "w-full"} h-full min-h-0 bg-color3 ${isTabletView ? "p-2" : "p-4"} rounded-sm flex flex-col`}>
                    <div className={`w-full flex ${isTabletView ? "gap-2 mb-1.5" : "gap-4 mb-4"} items-center justify-between ${isTabletView ? "pb-2" : "pb-4"}`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-sm" : "text-xl"}`}>Billing Details</span>
                        <Button className={`${isTabletView ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm"} bg-color13 font-semibold rounded-full`} disabled={updateRestricted}>Preview</Button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>Order Total </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(orderData?.order_total)}</span>
                    </div>
                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>Discount </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(discountAmount)}</span>
                    </div>
                    {
                        appliedDiscounts.map((discount, index) => (
                            <div key={`discount-${discount?.id || index}`} className={`w-full ${isTabletView ? "mb-0.5" : "mb-1"} flex gap-4 items-center justify-between`}>
                                <span className={`py-1 pl-3 text-color7 ${isTabletView ? "text-xs" : "text-sm"}`}>- {discount?.discount_name || discount?.name || "Discount"}</span>
                                <span className={`py-1 text-color7 ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(discount?.discount_amount)}</span>
                            </div>
                        ))
                    }
                    {orderData?.applied_spend_offer && (
                        <div className={`w-full ${isTabletView ? "mb-0.5" : "mb-1"} flex gap-4 items-center justify-between`}>
                            <span className={`py-1 pl-3 text-color7 font-medium ${isTabletView ? "text-xs" : "text-sm"} flex items-center gap-1`}>
                                - {orderData.applied_spend_offer.name}
                                {orderData.applied_spend_offer.discount_value && (
                                    <span className="text-[9px] bg-emerald-600/35 text-emerald-400 border border-emerald-500/50 px-1 py-0.2 rounded font-normal ml-1">
                                        {orderData.applied_spend_offer.discount_type === "percentage" ? `${orderData.applied_spend_offer.discount_value}%` : `£${formatAmount(orderData.applied_spend_offer.discount_value)}`}
                                    </span>
                                )}
                            </span>
                            <span className={`py-1 text-color7 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>
                                {orderData.applied_spend_offer.discount_amount > 0 
                                    ? `£ -${formatAmount(orderData.applied_spend_offer.discount_amount)}` 
                                    : "Free Item Applied"}
                            </span>
                        </div>
                    )}
                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>Loyalty Discount </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(loyaltyDiscountAmount)}</span>
                    </div>
                    {
                        appliedLoyaltyDiscounts.map((loyalty, index) => (
                            <div key={`loyalty-${loyalty?.loyalty_short_code || index}`} className={`w-full ${isTabletView ? "mb-0.5" : "mb-1"} flex gap-4 items-center justify-between`}>
                                <span className={`py-1 pl-3 text-color7 ${isTabletView ? "text-xs" : "text-sm"}`}>- {loyalty?.loyalty_short_code || loyalty?.loyalty_discount_code || "Code"}</span>
                                {
                                 (loyalty?.free_items && loyalty?.free_items.length > 0)?
                                 <span className={`py-1 text-color7 ${isTabletView ? "text-xs" : "text-sm"}`}>{loyalty?.free_items.length} x Free Item</span>
                                :
                                <span className={`py-1 text-color7 ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(loyalty?.loyalty_discount_amount)}</span>
                                }
                            </div>
                        ))
                    }
                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>Points ({orderData?.used_points || 0}) </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(orderData?.points_value)}</span>
                    </div>

                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>Service Charge </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(orderData?.service_charge_amount)}</span>
                    </div>
                    {
                        (orderData?.order_type === "delivery" || (orderData?.delivery_charge !== undefined && orderData?.delivery_charge !== null)) && (
                            <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                                <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>Delivery Charge </span>
                                <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(orderData?.delivery_charge)}</span>
                            </div>
                        )
                    }
                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>Total Savings </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(totalSavings)}</span>
                    </div>
                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}> Sub Total </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(orderData?.subtotal)}</span>
                    </div>
                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>Total Vat {vatInfo.vatTypeLabel}</span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(orderData?.total_vat)}</span>
                    </div>
                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <div className="flex items-center gap-1.5 min-w-0">
                            <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>Tips</span>
                            {tipsInfo.tipsMode === "course_wise" && (
                                <span className="bg-yellow-500/20 text-yellow-400 text-[10px] px-1.5 py-0.5 rounded font-medium">Course-wise</span>
                            )}
                            {tipsInfo.tipsMode === "split" && (
                                <span className="bg-blue-500/20 text-blue-400 text-[10px] px-1.5 py-0.5 rounded font-medium">Split</span>
                            )}
                        </div>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"} ${tipsInfo.totalTips > 0 ? "text-yellow-400 font-bold" : ""}`}>
                            £ {formatAmount(tipsAmount)}
                        </span>
                    </div>
                    {tipsInfo.breakdown && tipsInfo.breakdown.length > 0 && (
                        <div className={`w-full -mt-0.5 mb-1 pl-2 pr-1.5 py-1 bg-black/25 rounded border border-white/5 flex flex-col gap-0.5`}>
                            {tipsInfo.breakdown.map((person, idx) => (
                                <div key={idx} className="flex justify-between items-center text-[11px] text-gray-400">
                                    <span className="truncate">
                                        • {person.payerName} {person.tipType === "percentage" ? `(${person.tipInputAmount}%)` : `(Fixed)`}:
                                    </span>
                                    <span className="font-medium text-gray-300 shrink-0">
                                        £ {formatAmount(person.tipAmount)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    {advancePaidAmount > 0 && (
                        <div className={`w-full ${isTabletView ? "mt-0.5 pb-0.5" : "mt-1 pb-1"} flex gap-4 items-center justify-between `}>
                            <div className="flex items-center gap-1.5 min-w-0">
                                <span className={`py-1 font-bold ${isTabletView ? "text-xs text-emerald-400" : "text-emerald-400"} truncate`}>
                                    {advanceInfo.label || `Advance Paid`}
                                </span>
                                {advanceInfo.uniqueId && (
                                    <span className="bg-emerald-900/60 border border-emerald-600/40 text-[10px] px-1.5 py-0.2 rounded font-mono text-emerald-300 shrink-0">
                                        {advanceInfo.uniqueId}
                                    </span>
                                )}
                            </div>
                            <span className={`py-1 font-semibold text-emerald-400 shrink-0 ${isTabletView ? "text-xs" : "text-sm"}`}>- £ {formatAmount(advancePaidAmount)}</span>
                        </div>
                    )}
                    <div className={`w-full ${isTabletView ? "mt-0.5 pb-0.5" : "mt-1 pb-2"} flex gap-4 items-center justify-between border-b-2 border-dashed border-color4`}>
                        <span className={`py-1 font-bold ${isTabletView ? "text-xs text-color6" : "text-color6"}`}>Bill Amount </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(finalBillAmount)}</span>
                    </div>
                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>TOTAL TO PAY </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(effectiveTotalToPay)}</span>
                    </div>
                    <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>Paid </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(effectivePaidAmount)}</span>
                    </div>

                    {
                        changeAmount > 0 && (
                            <div className={`w-full ${isTabletView ? "mt-0.5 mb-0.5" : "mt-1 mb-1"} flex gap-4 items-center justify-between`}>
                                <span className={`py-1 font-semibold text-color7 ${isTabletView ? "text-xs" : "text-sm"}`}>Change Amount </span>
                                <span className={`py-1 font-semibold text-color7 ${isTabletView ? "text-xs" : "text-sm"}`}>£ {formatAmount(changeAmount)}</span>
                            </div>
                        )
                    }
                    <div className={`w-full ${isTabletView ? "mt-0.5 flex gap-4" : "mt-1 flex gap-4"} items-center justify-between`}>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs text-color14" : "text-color14"}`}>UNPAID AMOUNT  </span>
                        <span className={`py-1 font-semibold ${isTabletView ? "text-xs" : "text-sm"}`}>
                            £ {orderData?.payment_status === "paid" ? "0.00" : formatAmount(remainingDue)}
                        </span>
                    </div>
                    </div>
                </div>
            </div>
        </div>

        {
            isLoyaltyDiscountPopupOpen && (
                <LoyaltyDiscountPopup
                    orderId={orderData?.id}
                    orderData={orderData}
                    isTabletView={isTabletView}
                    updateRestricted={updateRestricted}
                    availableDiscounts={discountList}
                    appliedDiscounts={appliedDiscounts}
                    appliedLoyaltyDiscounts={appliedLoyaltyDiscounts}
                    onOrderUpdated={(updatedOrder) => dispatch(setOrderData(updatedOrder))}
                    onClose={() => setIsLoyaltyDiscountPopupOpen(false)}
                />
            )
        }

        {
            isPayByLinkPopupOpen && (
                <PayByLinkPopup
                    orderData={orderData}
                    isTabletView={isTabletView}
                    paymentAmount={remainingDue}
                    onClose={() => setIsPayByLinkPopupOpen(false)}
                    onSuccess={handlePayByLinkSubmit}
                />
            )
        }

        {
            isOrderActionPopupOpen && (
                <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center">
                    <div className={`${isTabletView ? "w-[520px]" : "w-[620px]"} bg-color2 border-2 border-color3 rounded-md p-5`}>
                        <h3 className={`${isTabletView ? "text-base" : "text-lg"} font-bold text-center mb-4`}>
                            {
                                isDeliveryOrder
                                    ? "Do you wanted to make this order cash on delivery and take payment when rider came back?"
                                    : "Do you wanted to complete this order without payment?"
                            }
                        </h3>

                        {
                            !isDeliveryOrder && (
                                <div className="mb-4">
                                    <label htmlFor="completeUnpaidReason" className="block text-sm font-semibold mb-2">
                                        Reason
                                    </label>
                                    <input
                                        id="completeUnpaidReason"
                                        type="text"
                                        value={completeUnpaidReason}
                                        onChange={(e) => setCompleteUnpaidReason(e.target.value)}
                                        disabled={isSubmittingOrderAction}
                                        placeholder="Enter reason for completing without payment"
                                        className="w-full py-2 px-3 bg-color3 border border-color3 rounded-md outline-none"
                                    />
                                </div>
                            )
                        }

                        <div className="flex gap-2 justify-center">
                            <button
                                type="button"
                                onClick={confirmOrderAction}
                                disabled={isSubmittingOrderAction || (!isDeliveryOrder && !completeUnpaidReason.trim())}
                                className={`px-6 py-2 bg-green-600 rounded-md font-bold ${(isSubmittingOrderAction || (!isDeliveryOrder && !completeUnpaidReason.trim())) ? "opacity-60 cursor-not-allowed" : ""}`}
                            >
                                {isSubmittingOrderAction ? "Processing..." : "Yes"}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsOrderActionPopupOpen(false);
                                    setCompleteUnpaidReason("");
                                }}
                                disabled={isSubmittingOrderAction}
                                className={`px-6 py-2 bg-red-600 rounded-md font-bold ${isSubmittingOrderAction ? "opacity-60 cursor-not-allowed" : ""}`}
                            >
                                No, Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )
        }

        <PendingPayLinkConflictDialog
            isOpen={isConflictDialogOpen}
            onClose={() => {
                setIsConflictDialogOpen(false);
                setConflictData(null);
            }}
            title="Active Payment Link Already Exists"
            orderLabel={`Order #${orderData?.id || ""} - £${parseFloat(remainingDue > 0 ? remainingDue : (orderData?.total_price || orderData?.order_total || 0)).toFixed(2)}`}
            existingPayLink={conflictData}
            targetChannel={conflictTargetChannel}
            onCancelAndResend={handleConflictCancelAndResend}
            onCancelOnly={handleConflictCancelOnly}
            isLoading={isConflictLoading}
        />

        <CancelOrderPayByLinkDialog
            isOpen={isCancelPayByLinkOpen}
            onClose={() => setIsCancelPayByLinkOpen(false)}
            onConfirm={handleCancelOrderPayByLink}
            isLoading={isCancelPayByLinkLoading}
            orderData={orderData}
            paymentAmount={remainingDue}
        />

        <OrderCardPaymentConfirmDialog
            isOpen={isCardPaymentConfirmOpen}
            onClose={() => setIsCardPaymentConfirmOpen(false)}
            onConfirm={handleTerminalCardPayment}
            isLoading={isCardPaymentLoading}
            orderData={orderData}
            paymentAmount={remainingDue}
        />

        <OrderCardPaymentResponseDialog
            isOpen={isCardPaymentDialogOpen}
            onClose={() => setIsCardPaymentDialogOpen(false)}
            responseData={cardPaymentResponseData}
            orderData={orderData}
            onPaymentCompleted={handleTerminalPaymentCompleted}
            onPaymentCancelled={handleTerminalPaymentCancelled}
        />

        <PaxPaymentTimeoutDialog
            isOpen={isPaxTimeoutDialogOpen}
            onClose={() => setIsPaxTimeoutDialogOpen(false)}
            onNewPayment={handleRetryTerminalPaymentFromTimeout}
            orderData={orderData}
            timeoutData={paxTimeoutData}
            isLoading={isCardPaymentLoading}
        />
        </>
    )
}

export default BillingPage;